/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  FlightLogProcess,
  VehicleRole,
  VehicleType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleProps {
  id: string;
  type: VehicleType;
  serialNumber: string | null;
  registrationNumber: string;
  dispatchCompanyId: string | null;
  year: number | null;
  isUnderLease: boolean;
  role: VehicleRole;
  dateAcquired: Date | null;
  makeId: string | null;
  modelId: string | null;
  cruiseSpeed: number | null;
  configurationClassId: string | null;
  isThirdPartyVehicle: boolean | null;
  parentUnitId: string | null;
  isHidden: boolean | null;
  faaCompanyId: string | null;
  flightLogProcess: FlightLogProcess;
  isPlaceholder: boolean;
  skytracIMEI: string | null;
  phoneNumber: string | null;
  stateInspectionRequired: boolean;
  completeFlightId: string | null;
  controlZoneId: string | null;
  communicationCenterId: string | null;
  regionId: string | null;
  isRetired: boolean;
  isPositionTrackingEnabled: boolean;
  vehicleDescription: string | null;
  hideFromOOSRUM: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleRecord extends Record<VehicleProps>({
  id: null as any,
  type: null as any,
  serialNumber: null as any,
  registrationNumber: null as any,
  dispatchCompanyId: null as any,
  year: null as any,
  isUnderLease: null as any,
  role: null as any,
  dateAcquired: null as any,
  makeId: null as any,
  modelId: null as any,
  cruiseSpeed: null as any,
  configurationClassId: null as any,
  isThirdPartyVehicle: null as any,
  parentUnitId: null as any,
  isHidden: null as any,
  faaCompanyId: null as any,
  flightLogProcess: null as any,
  isPlaceholder: null as any,
  skytracIMEI: null as any,
  phoneNumber: null as any,
  stateInspectionRequired: null as any,
  completeFlightId: null as any,
  controlZoneId: null as any,
  communicationCenterId: null as any,
  regionId: null as any,
  isRetired: null as any,
  isPositionTrackingEnabled: null as any,
  vehicleDescription: null as any,
  hideFromOOSRUM: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: VehicleType;
    serialNumber: string | null;
    registrationNumber: string;
    dispatchCompanyId: string | null;
    year: number | null;
    isUnderLease: boolean;
    role: VehicleRole;
    dateAcquired: string | number | Date | null;
    makeId: string | null;
    modelId: string | null;
    cruiseSpeed: number | null;
    configurationClassId: string | null;
    isThirdPartyVehicle: boolean | null;
    parentUnitId: string | null;
    isHidden: boolean | null;
    faaCompanyId: string | null;
    flightLogProcess: FlightLogProcess;
    isPlaceholder: boolean;
    skytracIMEI: string | null;
    phoneNumber: string | null;
    stateInspectionRequired: boolean;
    completeFlightId: string | null;
    controlZoneId: string | null;
    communicationCenterId: string | null;
    regionId: string | null;
    isRetired: boolean;
    isPositionTrackingEnabled: boolean;
    vehicleDescription: string | null;
    hideFromOOSRUM: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleRecord {
    if (json == null) {
      throw new Error('VehicleRecord.fromJS: parameter cannot be null');
    }
    return new VehicleRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', VehicleType.Unspecified),
        'VehicleJson.type',
      ),
      serialNumber: json.serialNumber,
      registrationNumber: getFromOrThrow(
        json,
        (j) => j.registrationNumber,
        'VehicleJson.registrationNumber',
      ),
      dispatchCompanyId: json.dispatchCompanyId,
      year: json.year,
      isUnderLease: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isUnderLease', false),
        'VehicleJson.isUnderLease',
      ),
      role: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'role', VehicleRole.Unspecified),
        'VehicleJson.role',
      ),
      dateAcquired: json.dateAcquired != null ? new Date(json.dateAcquired) : null,
      makeId: json.makeId,
      modelId: json.modelId,
      cruiseSpeed: json.cruiseSpeed,
      configurationClassId: json.configurationClassId,
      isThirdPartyVehicle: json.isThirdPartyVehicle,
      parentUnitId: json.parentUnitId,
      isHidden: json.isHidden,
      faaCompanyId: json.faaCompanyId,
      flightLogProcess: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'flightLogProcess', FlightLogProcess.External),
        'VehicleJson.flightLogProcess',
      ),
      isPlaceholder: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPlaceholder', false),
        'VehicleJson.isPlaceholder',
      ),
      skytracIMEI: json.skytracIMEI,
      phoneNumber: json.phoneNumber,
      stateInspectionRequired: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'stateInspectionRequired', false),
        'VehicleJson.stateInspectionRequired',
      ),
      completeFlightId: json.completeFlightId,
      controlZoneId: json.controlZoneId,
      communicationCenterId: json.communicationCenterId,
      regionId: json.regionId,
      isRetired: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isRetired', false),
        'VehicleJson.isRetired',
      ),
      isPositionTrackingEnabled: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPositionTrackingEnabled', false),
        'VehicleJson.isPositionTrackingEnabled',
      ),
      vehicleDescription: json.vehicleDescription,
      hideFromOOSRUM: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'hideFromOOSRUM', false),
        'VehicleJson.hideFromOOSRUM',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehicleJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: VehicleType;
    serialNumber: string | null;
    registrationNumber: string;
    dispatchCompanyId: string | null;
    year: number | null;
    isUnderLease: boolean;
    role: VehicleRole;
    dateAcquired: string | number | Date | null;
    makeId: string | null;
    modelId: string | null;
    cruiseSpeed: number | null;
    configurationClassId: string | null;
    isThirdPartyVehicle: boolean | null;
    parentUnitId: string | null;
    isHidden: boolean | null;
    faaCompanyId: string | null;
    flightLogProcess: FlightLogProcess;
    isPlaceholder: boolean;
    skytracIMEI: string | null;
    phoneNumber: string | null;
    stateInspectionRequired: boolean;
    completeFlightId: string | null;
    controlZoneId: string | null;
    communicationCenterId: string | null;
    regionId: string | null;
    isRetired: boolean;
    isPositionTrackingEnabled: boolean;
    vehicleDescription: string | null;
    hideFromOOSRUM: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', VehicleType.Unspecified),
            'VehicleJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      serialNumber: tryOrDefault(
        () => json.serialNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      registrationNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.registrationNumber, 'VehicleJson.registrationNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatchCompanyId: tryOrDefault(
        () => json.dispatchCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      year: tryOrDefault(
        () => json.year,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isUnderLease: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isUnderLease', false),
            'VehicleJson.isUnderLease',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      role: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'role', VehicleRole.Unspecified),
            'VehicleJson.role',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcquired: tryOrDefault(
        () => (json.dateAcquired != null ? new Date(json.dateAcquired) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      makeId: tryOrDefault(
        () => json.makeId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      modelId: tryOrDefault(
        () => json.modelId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cruiseSpeed: tryOrDefault(
        () => json.cruiseSpeed,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      configurationClassId: tryOrDefault(
        () => json.configurationClassId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isThirdPartyVehicle: tryOrDefault(
        () => json.isThirdPartyVehicle,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      parentUnitId: tryOrDefault(
        () => json.parentUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isHidden: tryOrDefault(
        () => json.isHidden,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      faaCompanyId: tryOrDefault(
        () => json.faaCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      flightLogProcess: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'flightLogProcess', FlightLogProcess.External),
            'VehicleJson.flightLogProcess',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPlaceholder: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPlaceholder', false),
            'VehicleJson.isPlaceholder',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      skytracIMEI: tryOrDefault(
        () => json.skytracIMEI,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      phoneNumber: tryOrDefault(
        () => json.phoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stateInspectionRequired: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'stateInspectionRequired', false),
            'VehicleJson.stateInspectionRequired',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      completeFlightId: tryOrDefault(
        () => json.completeFlightId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      controlZoneId: tryOrDefault(
        () => json.controlZoneId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isRetired: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isRetired', false),
            'VehicleJson.isRetired',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPositionTrackingEnabled: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPositionTrackingEnabled', false),
            'VehicleJson.isPositionTrackingEnabled',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleDescription: tryOrDefault(
        () => json.vehicleDescription,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hideFromOOSRUM: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'hideFromOOSRUM', false),
            'VehicleJson.hideFromOOSRUM',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehicleJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleViewModelBase implements VehicleProps {
  protected readonly _props: VehicleProps;
  private _json?: VehicleProps;

  get id(): string {
    return this._props.id;
  }
  get type(): VehicleType {
    return this._props.type;
  }
  get serialNumber(): string | null {
    return this._props.serialNumber;
  }
  get registrationNumber(): string {
    return this._props.registrationNumber;
  }
  get dispatchCompanyId(): string | null {
    return this._props.dispatchCompanyId;
  }
  get year(): number | null {
    return this._props.year;
  }
  get isUnderLease(): boolean {
    return this._props.isUnderLease;
  }
  get role(): VehicleRole {
    return this._props.role;
  }
  get dateAcquired(): Date | null {
    return this._props.dateAcquired;
  }
  get makeId(): string | null {
    return this._props.makeId;
  }
  get modelId(): string | null {
    return this._props.modelId;
  }
  get cruiseSpeed(): number | null {
    return this._props.cruiseSpeed;
  }
  get configurationClassId(): string | null {
    return this._props.configurationClassId;
  }
  get isThirdPartyVehicle(): boolean | null {
    return this._props.isThirdPartyVehicle;
  }
  get parentUnitId(): string | null {
    return this._props.parentUnitId;
  }
  get isHidden(): boolean | null {
    return this._props.isHidden;
  }
  get faaCompanyId(): string | null {
    return this._props.faaCompanyId;
  }
  get flightLogProcess(): FlightLogProcess {
    return this._props.flightLogProcess;
  }
  get isPlaceholder(): boolean {
    return this._props.isPlaceholder;
  }
  get skytracIMEI(): string | null {
    return this._props.skytracIMEI;
  }
  get phoneNumber(): string | null {
    return this._props.phoneNumber;
  }
  get stateInspectionRequired(): boolean {
    return this._props.stateInspectionRequired;
  }
  get completeFlightId(): string | null {
    return this._props.completeFlightId;
  }
  get controlZoneId(): string | null {
    return this._props.controlZoneId;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }
  get isRetired(): boolean {
    return this._props.isRetired;
  }
  get isPositionTrackingEnabled(): boolean {
    return this._props.isPositionTrackingEnabled;
  }
  get vehicleDescription(): string | null {
    return this._props.vehicleDescription;
  }
  get hideFromOOSRUM(): boolean {
    return this._props.hideFromOOSRUM;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAir() {
    return this._props.type === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.type === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.type === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.type === VehicleType.RotorWing;
  }
  public get isTypeUnspecified() {
    return this._props.type === VehicleType.Unspecified;
  }
  public get isRoleUnspecified() {
    return this._props.role === VehicleRole.Unspecified;
  }
  public get isPrimary() {
    return this._props.role === VehicleRole.Primary;
  }
  public get isSpare() {
    return this._props.role === VehicleRole.Spare;
  }
  public get isSupport() {
    return this._props.role === VehicleRole.Support;
  }
  public get isExternal() {
    return this._props.flightLogProcess === FlightLogProcess.External;
  }
  public get isFlightLogDynamic() {
    return this._props.flightLogProcess === FlightLogProcess.FlightLogDynamic;
  }
  public get isFlightLogStatic() {
    return this._props.flightLogProcess === FlightLogProcess.FlightLogStatic;
  }
  public get isPstStatic() {
    return this._props.flightLogProcess === FlightLogProcess.PstStatic;
  }

  public isAnyType(...vehicleType: VehicleType[]) {
    return vehicleType.includes(this._props.type);
  }
  public isAnyRole(...vehicleRole: VehicleRole[]) {
    return vehicleRole.includes(this._props.role);
  }
  public isAnyFlightLogProcess(...flightLogProcess: FlightLogProcess[]) {
    return flightLogProcess.includes(this._props.flightLogProcess);
  }

  constructor(props: VehicleProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        serialNumber: this._props.serialNumber,
        registrationNumber: this._props.registrationNumber,
        dispatchCompanyId: this._props.dispatchCompanyId,
        year: this._props.year,
        isUnderLease: this._props.isUnderLease,
        role: this._props.role,
        dateAcquired: this._props.dateAcquired,
        makeId: this._props.makeId,
        modelId: this._props.modelId,
        cruiseSpeed: this._props.cruiseSpeed,
        configurationClassId: this._props.configurationClassId,
        isThirdPartyVehicle: this._props.isThirdPartyVehicle,
        parentUnitId: this._props.parentUnitId,
        isHidden: this._props.isHidden,
        faaCompanyId: this._props.faaCompanyId,
        flightLogProcess: this._props.flightLogProcess,
        isPlaceholder: this._props.isPlaceholder,
        skytracIMEI: this._props.skytracIMEI,
        phoneNumber: this._props.phoneNumber,
        stateInspectionRequired: this._props.stateInspectionRequired,
        completeFlightId: this._props.completeFlightId,
        controlZoneId: this._props.controlZoneId,
        communicationCenterId: this._props.communicationCenterId,
        regionId: this._props.regionId,
        isRetired: this._props.isRetired,
        isPositionTrackingEnabled: this._props.isPositionTrackingEnabled,
        vehicleDescription: this._props.vehicleDescription,
        hideFromOOSRUM: this._props.hideFromOOSRUM,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleEntities = (state: AppState) => state.entityStores.vehicle.entities;

export const selectVehicleById = (id: string) => (state: AppState) =>
  state.entityStores.vehicle.entities.get(id);

export const selectVehicleMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicle.meta.get(id);

export const selectVehicleAndMetadataById = (id: string) =>
  createSelector(selectVehicleById(id), selectVehicleMetadataById(id), (vehicle, meta) => ({
    vehicle,
    meta,
  }));

export function selectVehicleEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleEntitiesByType(type: VehicleType) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectVehicleEntitiesBySerialNumber(serialNumber: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.serialNumber === serialNumber).valueSeq(),
  );
}

export function selectVehicleEntitiesByRegistrationNumber(registrationNumber: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.registrationNumber === registrationNumber).valueSeq(),
  );
}

export function selectVehicleEntitiesByDispatchCompanyId(dispatchCompanyId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.dispatchCompanyId === dispatchCompanyId).valueSeq(),
  );
}

export function selectVehicleEntitiesByYear(year: number) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.year === year).valueSeq(),
  );
}

export function selectVehicleEntitiesByIsUnderLease(isUnderLease: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.isUnderLease === isUnderLease).valueSeq(),
  );
}

export function selectVehicleEntitiesByRole(role: VehicleRole) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.role === role).valueSeq(),
  );
}

export function selectVehicleEntitiesByDateAcquired(dateAcquired: Date) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.dateAcquired === dateAcquired).valueSeq(),
  );
}

export function selectVehicleEntitiesByMakeId(makeId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.makeId === makeId).valueSeq(),
  );
}

export function selectVehicleEntitiesByModelId(modelId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.modelId === modelId).valueSeq(),
  );
}

export function selectVehicleEntitiesByCruiseSpeed(cruiseSpeed: number) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.cruiseSpeed === cruiseSpeed).valueSeq(),
  );
}

export function selectVehicleEntitiesByConfigurationClassId(configurationClassId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.configurationClassId === configurationClassId).valueSeq(),
  );
}

export function selectVehicleEntitiesByIsThirdPartyVehicle(isThirdPartyVehicle: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.isThirdPartyVehicle === isThirdPartyVehicle).valueSeq(),
  );
}

export function selectVehicleEntitiesByParentUnitId(parentUnitId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.parentUnitId === parentUnitId).valueSeq(),
  );
}

export function selectVehicleEntitiesByIsHidden(isHidden: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.isHidden === isHidden).valueSeq(),
  );
}

export function selectVehicleEntitiesByFaaCompanyId(faaCompanyId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.faaCompanyId === faaCompanyId).valueSeq(),
  );
}

export function selectVehicleEntitiesByFlightLogProcess(flightLogProcess: FlightLogProcess) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.flightLogProcess === flightLogProcess).valueSeq(),
  );
}

export function selectVehicleEntitiesByIsPlaceholder(isPlaceholder: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.isPlaceholder === isPlaceholder).valueSeq(),
  );
}

export function selectVehicleEntitiesBySkytracIMEI(skytracIMEI: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.skytracIMEI === skytracIMEI).valueSeq(),
  );
}

export function selectVehicleEntitiesByPhoneNumber(phoneNumber: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.phoneNumber === phoneNumber).valueSeq(),
  );
}

export function selectVehicleEntitiesByStateInspectionRequired(stateInspectionRequired: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.stateInspectionRequired === stateInspectionRequired).valueSeq(),
  );
}

export function selectVehicleEntitiesByCompleteFlightId(completeFlightId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.completeFlightId === completeFlightId).valueSeq(),
  );
}

export function selectVehicleEntitiesByControlZoneId(controlZoneId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.controlZoneId === controlZoneId).valueSeq(),
  );
}

export function selectVehicleEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectVehicleEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectVehicleEntitiesByIsRetired(isRetired: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.isRetired === isRetired).valueSeq(),
  );
}

export function selectVehicleEntitiesByIsPositionTrackingEnabled(
  isPositionTrackingEnabled: boolean,
) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.isPositionTrackingEnabled === isPositionTrackingEnabled).valueSeq(),
  );
}

export function selectVehicleEntitiesByVehicleDescription(vehicleDescription: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.vehicleDescription === vehicleDescription).valueSeq(),
  );
}

export function selectVehicleEntitiesByHideFromOOSRUM(hideFromOOSRUM: boolean) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.hideFromOOSRUM === hideFromOOSRUM).valueSeq(),
  );
}

export function selectVehicleEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehicleEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
